import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, X } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { openLeadPopup } from "./Navbar";
import { track } from "../lib/analytics";

const QUESTIONS = [
  { q: "Is your website less than 3 years old?", key: "modern" },
  { q: "Does it load in under 3 seconds on mobile data?", key: "speed" },
  { q: "Is it genuinely easy to use on a phone?", key: "mobile" },
  { q: "Does every page have one clear next step (CTA)?", key: "cta" },
  { q: "Can visitors reach you on WhatsApp or phone in one tap?", key: "contact" },
  { q: "Does each service you offer have its own page?", key: "seo_pages" },
  { q: "Have you published useful content in the last 60 days?", key: "content" },
  { q: "Do you have any automation capturing or following up leads?", key: "automation" },
];

const verdict = (score) => {
  if (score >= 75) return { label: "Strong Foundation", text: "Your website is ahead of most businesses. The opportunity now is growth — content, SEO and automation to multiply what's working." };
  if (score >= 40) return { label: "Leaking Enquiries", text: "Your website works but loses potential customers at several points. Targeted fixes would likely pay for themselves within months." };
  return { label: "Costing You Business", text: "Every month with these gaps is another month of missed enquiries going to competitors. A focused rebuild is likely your highest-ROI move." };
};

export default function GrowthScore() {
  const [step, setStep] = useState(-1);
  const [answers, setAnswers] = useState([]);

  const answer = (yes) => {
    const next = [...answers, yes];
    setAnswers(next);
    if (next.length === QUESTIONS.length) {
      const score = Math.round((next.filter(Boolean).length / QUESTIONS.length) * 100);
      track("growth_score_complete", { score });
      setStep(QUESTIONS.length);
    } else {
      setStep(step + 1);
    }
  };

  const start = () => {
    track("growth_score_start", {});
    setStep(0);
  };

  const score = Math.round((answers.filter(Boolean).length / QUESTIONS.length) * 100);
  const v = verdict(score);

  return (
    <section className="bg-bone py-20 text-obsidian lg:py-28" data-testid="growth-score-section">
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-8">
        <Chapter index="—" label="Interactive Tool" dark />
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-display text-3xl font-bold leading-[1.05] tracking-tight sm:text-4xl">
            See Where Your Website Is Losing Potential Customers.
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-obsidian/55">
            Eight quick questions. Get your Grotech Website Growth Score — an honest snapshot of how
            your website performs as a business tool.
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-10 rounded-3xl border border-obsidian/10 bg-white p-8 shadow-sm sm:p-10">
            {step === -1 && (
              <button
                data-testid="growth-score-start"
                onClick={start}
                className="group flex items-center gap-2 rounded-full bg-ember px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover mx-auto"
              >
                Check My Score <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            )}
            <AnimatePresence mode="wait">
              {step >= 0 && step < QUESTIONS.length && (
                <motion.div
                  key={step}
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -24 }}
                  transition={{ duration: 0.3 }}
                  data-testid="growth-score-question"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">
                    Question {step + 1} of {QUESTIONS.length}
                  </p>
                  <p className="mt-4 font-display text-xl font-bold tracking-tight sm:text-2xl">
                    {QUESTIONS[step].q}
                  </p>
                  <div className="mt-7 flex justify-center gap-4">
                    <button
                      data-testid="growth-score-yes"
                      onClick={() => answer(true)}
                      className="flex items-center gap-2 rounded-full bg-obsidian px-8 py-3.5 font-display text-sm font-bold text-white transition-transform hover:scale-105"
                    >
                      <Check className="h-4 w-4" /> Yes
                    </button>
                    <button
                      data-testid="growth-score-no"
                      onClick={() => answer(false)}
                      className="flex items-center gap-2 rounded-full border border-obsidian/20 px-8 py-3.5 font-display text-sm font-bold text-obsidian transition-colors hover:bg-obsidian/5"
                    >
                      <X className="h-4 w-4" /> No
                    </button>
                  </div>
                </motion.div>
              )}
              {step === QUESTIONS.length && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                  data-testid="growth-score-result"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-ember">
                    Grotech Website Growth Score
                  </p>
                  <p className="mt-4 font-display text-6xl font-black tracking-tight sm:text-7xl">
                    {score}
                    <span className="text-2xl text-obsidian/40">/100</span>
                  </p>
                  <p className="mt-2 font-display text-xl font-bold text-ember">{v.label}</p>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-obsidian/60">{v.text}</p>
                  <p className="mt-6 text-sm font-semibold">Want us to review your website personally?</p>
                  <button
                    data-testid="growth-score-cta"
                    onClick={openLeadPopup}
                    className="group mt-4 inline-flex items-center gap-2 rounded-full bg-ember px-7 py-3.5 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover"
                  >
                    Get My Free Review <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </button>
                  <p className="mt-4 text-[11px] text-obsidian/35">
                    A practical self-assessment — not an official Google score.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
